import React from "react";
import { Box, Button, CircularProgress } from "@mui/material";

/**
 * A component with optional icon and text.
 *
 * @param {Object} props - The props for the ActionButton component.
 * @param {string} [props.buttonText] - The text label to display inside the button. Defaults to "save" when not provided.
 * @param {Function} [props.onButtonClick] - Callback invoked when the button is clicked.
 * @param {React.ComponentType} [props.buttonIcon] - A React component to render as the start icon of the button.
 * @param {'button' | 'submit' | 'reset'} [props.type="button"] - The HTML button type attribute (e.g. "button", "submit", "reset").
 * @param {'contained' | 'outlined' | 'text'} [props.variant="contained"] - The MUI button variant to use (e.g. "contained", "outlined", "text").
 * @param {'small' | 'medium' | 'large'} [props.size="medium"] - The MUI button size (e.g. "small", "medium", "large").
 * @param {boolean} [props.loading=false] - If true, shows a loading spinner and disables the button.
 * @param {boolean} [props.disabled=false] - If true, disables the button.
 * @param {Object} [props.sx={}] - Additional MUI `sx` style overrides applied to the button.
 */
export const ActionButton = ({
  buttonText = "save",
  onButtonClick = () => {},
  buttonIcon: Icon,
  type = "button",
  variant = "contained",
  size = "medium",
  loading = false,
  disabled = false,
  sx = {},
} = {}) => {
  const handleButtonClick = () => {
    onButtonClick();
  };

  const customStyles =
    variant === "contained"
      ? {
          backgroundColor: "#674BF5",
          color: "#fff",
          "&.Mui-disabled": {
            backgroundColor: "#7D5DF7",
            color: "#fff",
            opacity: 0.6,
          },
        }
      : {
          color: "#674BF5",
          borderColor: "#674BF5",
          "&.Mui-disabled": {
            color: "#7D5DF7",
            borderColor: "#7D5DF7",
            opacity: 0.6,
          },
        };

  return (
    <Button
      onClick={handleButtonClick}
      type={type}
      variant={variant}
      size={size}
      startIcon={!loading && Icon ? <Icon /> : null}
      disabled={disabled || loading}
      sx={{ ...customStyles, ...sx }}
    >
      <Box sx={{ position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
        <Box sx={{ opacity: loading ? 0.35 : 1 }}>{buttonText}</Box>
        {loading ? (
          <CircularProgress
            size={16}
            color="inherit"
            sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}
          />
        ) : null}
      </Box>
    </Button>
  );
};
